import { useEffect, useRef, useState } from "react";
import { fetchReviews, createReview, deleteReview } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Star, Trash2, MessageSquare, ChevronLeft, ChevronRight } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

function StarRow({ value, onChange, size = 5, readonly }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => {
        const active = n <= value;
        return (
          <button
            key={n}
            type="button"
            disabled={readonly}
            onClick={() => onChange?.(n)}
            data-testid={`star-${n}`}
            className={`${readonly ? "cursor-default" : "hover:scale-110"} p-0.5`}
            style={{ transition: "transform 150ms ease" }}
            aria-label={`${n} estrellas`}
          >
            <Star
              className={`w-${size} h-${size} ${active ? "fill-[#ff4500] text-[#ff4500]" : "text-neutral-600"}`}
              style={{ width: size * 4, height: size * 4 }}
            />
          </button>
        );
      })}
    </div>
  );
}

export default function Reviews() {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [avg, setAvg] = useState(0);
  const [count, setCount] = useState(0);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollerRef = useRef(null);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const step = Math.min(el.clientWidth * 0.9, 640);
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  const load = async () => {
    try {
      const d = await fetchReviews();
      setReviews(d.reviews);
      setAvg(d.avg_rating);
      setCount(d.count);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const [paused, setPaused] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (paused || reviews.length < 2) return;
    const t = setInterval(() => {
      const el = scrollerRef.current;
      if (!el) return;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 8;
      if (atEnd) el.scrollTo({ left: 0, behavior: "smooth" });
      else el.scrollBy({ left: (el.firstElementChild?.offsetWidth || 300) + 12, behavior: "smooth" });
    }, 3500);
    return () => clearInterval(t);
  }, [paused, reviews.length]);

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el || !el.firstElementChild) return;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 8;
    setActive(atEnd ? Math.min(reviews.length, 12) - 1 : Math.round(el.scrollLeft / (el.firstElementChild.offsetWidth + 12)));
  };

  const goTo = (i) => {
    const el = scrollerRef.current;
    if (!el || !el.firstElementChild) return;
    el.scrollTo({ left: i * (el.firstElementChild.offsetWidth + 12), behavior: "smooth" });
  };

  async function onSubmit(e) {
    e.preventDefault();
    if (!user) {
      toast.info("Iniciá sesión para dejar tu reseña");
      startGoogleLogin();
      return;
    }
    if (text.trim().length < 3) {
      toast.error("Escribí un comentario más largo");
      return;
    }
    setSubmitting(true);
    try {
      await createReview(rating, text.trim());
      toast.success("¡Gracias por tu reseña!");
      setText("");
      setRating(5);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al enviar reseña");
    } finally {
      setSubmitting(false);
    }
  }

  async function onDelete(id) {
    if (!window.confirm("¿Eliminar esta reseña?")) return;
    try {
      await deleteReview(id);
      toast.success("Reseña eliminada");
      load();
    } catch {
      toast.error("Error al eliminar");
    }
  }

  return (
    <section id="resenas" className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="text-xs tracking-[0.3em] text-[#ff4500]">RESEÑAS</div>
          <h2 className="font-display text-4xl sm:text-5xl tracking-wider">Lo que dicen los jugadores</h2>
        </div>
        {count > 0 && (
          <div className="flex items-center gap-2 text-sm">
            <StarRow value={Math.round(avg || 0)} readonly size={4} />
            <span className="font-display text-2xl tracking-wider">{Number(avg || 0).toFixed(1)}</span>
            <span className="text-neutral-400">/ 5 · {count} reseñas</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form */}
        <form onSubmit={onSubmit} className="lg:col-span-1 rounded-xl border border-[#ff4500]/30 bg-[#0d0d0d] p-5 h-fit">
          <div className="flex items-center gap-2 mb-3">
            <MessageSquare className="w-5 h-5 text-[#ff4500]" />
            <div className="font-display text-2xl tracking-wider">Dejá tu reseña</div>
          </div>
          {!user && (
            <p className="text-xs text-neutral-400 mb-3">
              Iniciá sesión con Google para publicar tu comentario.
            </p>
          )}
          <div className="mb-3">
            <label className="block text-xs uppercase tracking-widest text-neutral-400 mb-2">
              Puntuación
            </label>
            <StarRow value={rating} onChange={setRating} size={6} />
          </div>
          <div className="mb-4">
            <label className="block text-xs uppercase tracking-widest text-neutral-400 mb-2">
              Tu experiencia
            </label>
            <textarea
              data-testid="review-text-input"
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, 500))}
              placeholder="Contá cómo fue tu recarga..."
              rows={4}
              className="input-field w-full px-3 py-2 rounded-md text-sm resize-none"
            />
            <div className="text-[10px] text-neutral-500 text-right mt-1">{text.length}/500</div>
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="submit-review-btn"
            className="btn-primary w-full px-4 py-2.5 rounded-md text-sm"
          >
            {submitting ? "Enviando..." : user ? "Publicar reseña" : "Iniciar sesión y publicar"}
          </button>
        </form>

        {/* Horizontal scroll list */}
        <div className="lg:col-span-2 relative min-w-0">
          {reviews.length > 0 && (
            <div className="hidden sm:flex absolute -top-14 right-0 gap-2">
              <button
                onClick={() => scrollBy(-1)}
                data-testid="reviews-scroll-left"
                className="w-9 h-9 rounded-md bg-white/5 hover:bg-white/10 grid place-items-center"
                aria-label="Anterior"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => scrollBy(1)}
                data-testid="reviews-scroll-right"
                className="w-9 h-9 rounded-md bg-white/5 hover:bg-white/10 grid place-items-center"
                aria-label="Siguiente"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {loading ? (
            <div className="text-neutral-400">Cargando reseñas...</div>
          ) : reviews.length === 0 ? (
            <div className="rounded-xl border border-white/10 bg-[#0d0d0d] p-6 text-center text-neutral-400">
              Aún no hay reseñas. Sé el primero en compartir tu experiencia.
            </div>
          ) : (
            <div
              ref={scrollerRef}
              data-testid="reviews-scroller"
              onScroll={onScroll}
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
              onTouchStart={() => setPaused(true)}
              onTouchEnd={() => setTimeout(() => setPaused(false), 4000)}
              className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-3 -mx-1 px-1 no-scrollbar"
              style={{ scrollbarWidth: "none" }}
            >
              {reviews.map((r) => (
                <div
                  key={r.review_id}
                  data-testid={`review-${r.review_id}`}
                  className="shrink-0 w-[280px] sm:w-[320px] snap-start rounded-lg border border-white/10 bg-[#0d0d0d] p-4 relative"
                >
                  {user?.is_admin && (
                    <button
                      onClick={() => onDelete(r.review_id)}
                      data-testid={`delete-review-${r.review_id}`}
                      className="absolute top-2 right-2 p-1 rounded hover:bg-white/10 text-neutral-500 hover:text-rose-400"
                      aria-label="Eliminar reseña"
                      title="Eliminar (admin)"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <div className="flex items-center gap-2 mb-2">
                    {r.user_picture ? (
                      <img src={r.user_picture} alt="" className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-[#ff4500]/20 grid place-items-center text-xs text-[#ff4500] font-bold">
                        {(r.user_name || "?").slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{r.user_name}</div>
                      <div className="text-[11px] text-neutral-500">
                        {new Date(r.created_at).toLocaleDateString("es-AR", { day: "numeric", month: "short", year: "numeric" })}
                      </div>
                    </div>
                  </div>
                  <StarRow value={r.rating} readonly size={3} />
                  <p className="mt-2 text-sm text-neutral-300 leading-relaxed break-words line-clamp-6">{r.text}</p>
                </div>
              ))}
            </div>
          )}
          {reviews.length > 1 && (
            <div className="mt-2 flex justify-center gap-1.5" data-testid="reviews-dots">
              {reviews.slice(0, 12).map((r, i) => (
                <button
                  key={r.review_id}
                  onClick={() => goTo(i)}
                  data-testid={`reviews-dot-${i}`}
                  aria-label={`Reseña ${i + 1}`}
                  className={`h-1.5 rounded-full ${i === active ? "w-6 bg-[#ff4500]" : "w-1.5 bg-white/20"}`}
                  style={{ transition: "width 200ms ease, background-color 200ms ease" }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
